import {
  Button,
  Card,
  CardBody,
  Chip,
  Progress,
  Spinner,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useLocation } from 'wouter';
import type {
  TrackingActivationShipment,
  TrackingActivationState,
} from 'common/src/api/shipment-tracking-activation';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { StatusChip } from '../ui';
import { getTrackingEventLabel } from '../../utils/shipment-tracking-display';
import useTrackingActivationStore, {
  type ActivationItem,
} from '../../store/useTrackingActivationStore';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "2026-11-12…" → "12 nov" (calendar day as written, no timezone shift). */
const day = (value: string | null | undefined, language: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!match) return null;
  return new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(Date.UTC(+match[1], +match[2] - 1, +match[3]));
};

/** AWB as 045-53258225; containers as they are. */
const formatIdentifier = (value: string) =>
  /^\d{11}$/.test(value) ? `${value.slice(0, 3)}-${value.slice(3)}` : value;

const STATE: Record<
  TrackingActivationState,
  { label: string; tone: 'success' | 'info' | 'warning' | 'danger' | 'default' }
> = {
  tracking: { label: 'En seguimiento', tone: 'success' },
  locating: { label: 'Ubicando', tone: 'info' },
  scheduled: { label: 'Programado', tone: 'default' },
  queued: { label: 'En cola', tone: 'default' },
  not_found: { label: 'No encontrado', tone: 'danger' },
  no_identifier: { label: 'Sin contenedor', tone: 'warning' },
  invalid_identifier: { label: 'Número inválido', tone: 'warning' },
  no_dates: { label: 'Sin fechas', tone: 'warning' },
  expired: { label: 'Fuera de plazo', tone: 'default' },
  error: { label: 'Reintentando', tone: 'warning' },
};

const REVIEW_STATES: TrackingActivationState[] = [
  'not_found',
  'no_identifier',
  'invalid_identifier',
  'no_dates',
];

const DETAIL_TONE: Partial<Record<TrackingActivationState, string>> = {
  not_found: 'text-danger',
  no_identifier: 'text-warning-600',
  invalid_identifier: 'text-warning-600',
  no_dates: 'text-warning-600',
};

function detail(
  result: TrackingActivationShipment | null,
  t: Translate,
  language: string
): string {
  const state = result?.state ?? 'queued';
  switch (state) {
    case 'tracking': {
      const event = result?.latest_event;
      const eta = day(result?.live_eta, language);
      return [
        event
          ? [t(getTrackingEventLabel(event.event_type)), event.location_name]
              .filter(Boolean)
              .join(' · ')
          : t('Reserva confirmada'),
        eta ? `ETA ${eta}` : null,
        // Skip the destination when the latest milestone already happened there.
        result?.destination_name &&
        result.destination_name.toLowerCase() !==
          event?.location_name?.toLowerCase()
          ? result.destination_name
          : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'locating':
      return t('ShipsGo lo está ubicando; los hitos aparecen en minutos.');
    case 'scheduled': {
      const opens = day(result?.registration_opens_on, language);
      return opens
        ? t('Se activa el {{day}}, día del zarpe.', { day: opens })
        : t('Se activa el día del zarpe.');
    }
    case 'queued':
      return t('Se activa en los próximos minutos.');
    case 'not_found':
      return t('No encontrado en ShipsGo: revisa el número.');
    case 'no_identifier':
      return t('Agrega el contenedor o AWB para seguirlo.');
    case 'invalid_identifier':
      return t('El contenedor o AWB no es válido.');
    case 'no_dates':
      return t('Falta ETD o ETA en el despacho.');
    case 'expired':
      return t('Arribó hace más de 10 días; ya no se sigue.');
    default:
      return t('No se pudo activar ahora; se reintenta solo.');
  }
}

const Row = ({ item }: { item: ActivationItem }) => {
  const { t, currentLanguage } = useTranslation('shipments');
  const state = item.result?.state ?? 'queued';
  // An 11-digit identifier is an AWB even before the server answers.
  const air =
    (item.result?.mode ?? (/^\d{11}$/.test(item.label) ? 'air' : 'ocean')) ===
    'air';
  return (
    <div className='flex flex-col gap-1 border-b border-default-100 px-4 py-2.5 last:border-b-0'>
      <div className='flex items-center gap-2'>
        <Icon
          icon={air ? 'tabler:plane' : 'tabler:ship'}
          width={15}
          className='shrink-0 text-default-400'
        />
        <span className='min-w-0 truncate font-mono text-sm font-semibold'>
          {formatIdentifier(item.result?.identifier ?? item.label)}
        </span>
        <span className='ml-auto shrink-0'>
          {item.pending ? (
            <Spinner size='sm' />
          ) : (
            <StatusChip tone={STATE[state].tone}>
              {t(STATE[state].label)}
            </StatusChip>
          )}
        </span>
      </div>
      <p
        className={`flex items-center gap-1.5 pl-[23px] text-xs ${
          item.pending
            ? 'text-default-500'
            : (DETAIL_TONE[state] ?? 'text-default-500')
        }`}
      >
        {item.pending
          ? t('Buscando en ShipsGo…')
          : detail(item.result, t, currentLanguage)}
        {!item.pending && item.result?.has_route && (
          <Icon
            icon='tabler:map-pin'
            width={13}
            role='img'
            className='shrink-0 text-success'
            aria-label={t('Ruta en el mapa')}
          />
        )}
      </p>
    </div>
  );
};

/** Live progress of tracking activation after an upload (global, bottom right). */
const TrackingActivationPanel = () => {
  const { t } = useTranslation('shipments');
  const { job, dismiss } = useTrackingActivationStore();
  const [, setLocation] = useLocation();
  if (!job) return null;

  const done = job.items.filter((item) => !item.pending).length;
  const count = (states: TrackingActivationState[]) =>
    job.items.filter(
      (item) => !item.pending && states.includes(item.result?.state ?? 'queued')
    ).length;
  const tracking = count(['tracking', 'locating']);
  const scheduled = count(['scheduled']);
  const queued = count(['queued', 'error']) + job.deferred;
  const review = count(REVIEW_STATES);
  const summary = [
    tracking && t('{{count}} en seguimiento', { count: tracking }),
    scheduled &&
      t(scheduled === 1 ? '{{count}} programado' : '{{count}} programados', {
        count: scheduled,
      }),
    queued && t('{{count}} en cola', { count: queued }),
    review && t('{{count}} por revisar', { count: review }),
  ]
    .filter(Boolean)
    .join(' · ');
  const allNeedReview = !job.running && tracking === 0 && review > 0;

  return (
    // design-ignore: panel flotante (design.md › Cards); necesita elevación propia
    <Card
      shadow='lg'
      role='region'
      aria-label={t('Activación de seguimiento')}
      className='fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-50 w-[min(380px,calc(100vw-2rem))]'
    >
      <CardBody className='gap-0 p-0'>
        <div className='flex items-start gap-3 px-4 pb-3 pt-4'>
          <span
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
              allNeedReview
                ? 'bg-warning/15 text-warning-600'
                : 'bg-success/15 text-success'
            }`}
          >
            <Icon
              icon={
                job.running
                  ? 'tabler:radar-2'
                  : allNeedReview
                    ? 'tabler:alert-circle'
                    : 'tabler:circle-check'
              }
              width={18}
              className={job.running ? 'animate-pulse' : undefined}
            />
          </span>
          <div className='min-w-0 flex-1'>
            <p className='flex items-center gap-2 text-sm font-semibold'>
              {job.running
                ? t('Activando seguimiento')
                : allNeedReview
                  ? t('Revisa los despachos')
                  : t('Seguimiento activado')}
              {job.preview && (
                <Chip size='sm' variant='flat'>
                  {t('Vista previa')}
                </Chip>
              )}
            </p>
            <p className='mt-0.5 text-xs text-default-500' aria-live='polite'>
              {job.running
                ? t(
                    '{{done}} de {{total}} · consultando a las navieras y aerolíneas',
                    {
                      done,
                      total: job.items.length,
                    }
                  )
                : summary || t('Sin contenedores para seguir')}
            </p>
          </div>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            aria-label={t('Cerrar')}
            onPress={dismiss}
          >
            <Icon icon='tabler:x' width={16} />
          </Button>
        </div>
        <Progress
          size='sm'
          aria-label={t('Progreso de activación')}
          value={job.items.length ? (done / job.items.length) * 100 : 100}
          color={!job.running && review > 0 ? 'warning' : 'success'}
          className='px-4'
        />
        <div className='mt-2 max-h-[min(320px,35vh)] overflow-y-auto border-t border-default-200 sm:max-h-[min(320px,50vh)]'>
          {job.items.map((item) => (
            <Row key={item.key} item={item} />
          ))}
        </div>
        {job.deferred > 0 && (
          <p className='border-t border-default-200 px-4 py-2 text-xs text-default-500'>
            {t('Otros {{count}} se activan en los próximos minutos.', {
              count: job.deferred,
            })}
          </p>
        )}
        {!job.running && tracking > 0 && (
          <div className='flex justify-end border-t border-default-200 px-3 py-2'>
            <Button
              size='sm'
              variant='flat'
              endContent={<Icon icon='tabler:arrow-right' width={15} />}
              onPress={() => {
                dismiss();
                setLocation('/shipments/arrivals');
              }}
            >
              {t('Ver en Gestor de Arribos')}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default TrackingActivationPanel;
